import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import type { FormEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { loginRequest } from '../api/auth'
import { isApiError, shouldShowApiErrorToast } from '../api/errors'
import { chromeInputClass } from '../components/ChromeSurface'
import {
  LOGIN_CARD_SOFT_BLUR_THEMES,
  LoginAuthCard,
  LoginAuthCardBody,
  loginAuthMutedClasses,
  loginFieldRound,
  LoginHeroPanel,
  loginPrimaryButtonRound,
} from '../components/login'
import { ThemeMobileHeaderControl } from '../components/theme'
import { ThemedImage } from '../components/ThemedImage'
import { loginBackgroundSrc, loginScrimBackground } from '../constants/loginBackgrounds'
import { useAuthSession } from '../context/AuthSessionContext'
import { useToast } from '../context/ToastContext'
import { resolvePostLoginPath } from '../lib/postLoginPath'
import type { ThemeId } from '../theme/ThemeContext'
import { useTheme } from '../theme/ThemeContext'

/** 与各主题配套的副标题：凝练概括气质，不出现「登录」二字；勿尾句号 */
const LOGIN_HERO_LINES: Partial<Record<ThemeId, string>> = {
  warm: '灰纸锈褐偏印刷翻阅手感',
  cool: '青雾银灰铺底亮青作主高光',
  dark: '暗色蓝灰底子长时间盯屏不累',
  glass: '暗磨砂层次电青偏科幻座舱界面',
  cartoon: '奶油底咖啡粗边阴影整块翘起',
  illustration: '水彩纸本赭红软线晕染不发灰',
  pixel: '深蓝夜幕琥珀钮薄荷直角描边',
  geek: '黑屏一行绿荧光等宽终端味',
}

/** 登录卡片内：14px 级输入 / 12px 级辅助（不改动左侧大标题与背景层） */

function IconWeChat(props: { className?: string }) {
  return (
    <svg className={props.className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M9.5 4C5.36 4 2 6.69 2 10.05c0 1.74 1 3.31 2.6 4.35-.06.35-.35 1.36-.35 1.36s-.02.05.02.07c.04.02.09.01.09.01l1.56-.91c.63.22 1.31.34 2.02.34.17 0 .34-.01.5-.03-.09-.43-.14-.88-.14-1.34 0-3.13 3.13-5.66 7-5.66.62 0 1.21.08 1.78.22C15.54 5.78 12.86 4 9.5 4zm-2 4.5c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm4 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm8 2.55c0-2.8-2.85-5.05-6.35-5.05-3.5 0-6.35 2.25-6.35 5.05 0 2.8 2.85 5.05 6.35 5.05.71 0 1.39-.09 2.02-.25l1.85 1.07s.06.04.09.01c.04-.03.02-.08.02-.08s-.35-1.18-.42-1.55c1.35-.95 2.24-2.35 2.24-3.95zm-8.5 1.45c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm3.5 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"
      />
    </svg>
  )
}

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { showToast } = useToast()
  const { isLoggedIn, markLoggedIn, hydrateFromStorage } = useAuthSession()
  const { theme, themeOptions } = useTheme()
  const themeMeta = themeOptions.find((t) => t.id === theme)
  const angular = theme === 'pixel' || theme === 'geek'
  const isGlass = theme === 'glass'
  const { muted: cardMuted, mutedMid: cardMutedMid, divider: cardDivider } =
    loginAuthMutedClasses(theme)
  const loginCardSoftBlur = LOGIN_CARD_SOFT_BLUR_THEMES.has(theme)
  const fieldRound = loginFieldRound(theme)
  const btnRound = loginPrimaryButtonRound(theme)
  const reducedMotion = useReducedMotion()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [busy, setBusy] = useState(false)
  const passwordInputRef = useRef<HTMLInputElement>(null)

  const fromState = (location.state as { from?: { pathname?: string } } | null)?.from
    ?.pathname

  useEffect(() => {
    hydrateFromStorage()
  }, [hydrateFromStorage])

  useEffect(() => {
    if (!isLoggedIn) return
    navigate(resolvePostLoginPath(fromState), { replace: true })
  }, [fromState, isLoggedIn, navigate])

  const loginWithCredentials = async (u: string, pass: string) => {
    const name = u.trim()
    if (!name || !pass.trim()) {
      showToast({ message: '请输入用户名和密码', variant: 'warning' })
      return
    }
    setBusy(true)
    try {
      const data = await loginRequest({ username: name, password: pass.trim() })
      markLoggedIn(data)
      showToast({
        message: data.nickname ? `欢迎，${data.nickname}` : '欢迎回来',
        variant: 'success',
        autoCloseMs: 2000,
      })
      navigate(resolvePostLoginPath(fromState), { replace: true })
    } catch (err) {
      passwordInputRef.current?.focus()
      passwordInputRef.current?.select()
      if (!shouldShowApiErrorToast(err)) {
        const msg = isApiError(err) ? err.message : '用户名或密码错误'
        showToast({ message: msg, variant: 'warning' })
      } else {
        const msg = isApiError(err) ? err.message : '登录失败，请稍后重试'
        showToast({ message: msg, variant: 'warning' })
      }
    } finally {
      setBusy(false)
    }
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    void loginWithCredentials(username, password)
  }

  const oauthSoon = (label: string) => {
    showToast({ message: `${label} 登录即将开放`, variant: 'info' })
  }

  const inputShell = [
    chromeInputClass(angular),
    '!mt-0 py-3 text-[0.875rem] leading-normal',
    loginCardSoftBlur ? 'placeholder:text-ink/45' : 'placeholder:text-ink-soft/80',
    fieldRound,
    angular ? '' : '!shadow-sm',
  ].join(' ')

  return (
    <div
      data-login-shell
      className="fixed inset-0 z-[5] flex h-dvh max-h-dvh min-h-0 flex-col overflow-hidden bg-[var(--surface)]"
    >
      <section className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="pointer-events-none absolute inset-0 min-h-0 min-w-0 overflow-hidden">
          <AnimatePresence initial={false} mode="sync">
            <motion.div
              key={`login-bg-${theme}`}
              className="absolute inset-0 overflow-hidden"
              initial={{
                opacity: 0,
                scale: reducedMotion ? 1 : 1.02,
                filter: reducedMotion ? 'blur(0px)' : 'blur(10px)',
              }}
              animate={{
                opacity: 1,
                scale: 1,
                filter: 'blur(0px)',
              }}
              exit={{
                opacity: 0,
                scale: reducedMotion ? 1 : 1.015,
                filter: reducedMotion ? 'blur(0px)' : 'blur(9px)',
              }}
              transition={{
                duration: reducedMotion ? 0.2 : 0.48,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <ThemedImage
                src={loginBackgroundSrc(theme)}
                alt=""
                fallbackLabel="背景图无法加载"
                className="block h-full min-h-full w-full object-cover object-center"
                decoding="async"
              />
              <motion.div
                className="absolute inset-0"
                initial={{ opacity: 0.82 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: reducedMotion ? 0.2 : 0.45,
                  ease: [0.22, 1, 0.36, 1],
                }}
                style={{ background: loginScrimBackground(theme) }}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        <header
          className={[
            'relative z-30 flex min-w-0 shrink-0 items-center justify-between gap-[calc(12*var(--rpx))] px-[calc(24*var(--rpx))] py-[calc(12*var(--rpx))] sm:gap-3 sm:px-6 sm:py-3 lg:absolute lg:left-0 lg:right-0 lg:top-0 lg:z-40 lg:gap-3 lg:bg-transparent lg:px-8 lg:py-4',
            isGlass
              ? 'border-b border-white/[0.08] bg-[var(--panel)]/30 backdrop-blur-md lg:border-b-0 lg:bg-transparent lg:backdrop-blur-none'
              : 'border-b border-line/40 bg-[var(--surface)]/50 backdrop-blur-md lg:border-b-0 lg:bg-transparent lg:backdrop-blur-none',
          ].join(' ')}
        >
          <Link
            to="/"
            className="min-w-0 shrink font-display text-lg font-semibold tracking-tight text-ink drop-shadow-sm"
          >
            GlyphicAI
          </Link>
          <ThemeMobileHeaderControl />
        </header>

        <main
          className={[
            'relative z-10 flex min-h-0 min-w-0 flex-1 flex-col',
            'max-lg:scroll-region max-lg:min-h-0 max-lg:overflow-y-auto max-lg:overscroll-y-contain lg:overflow-hidden',
            'px-[calc(28*var(--rpx))] pb-[max(calc(16*var(--rpx)),env(safe-area-inset-bottom))]',
            'pt-[calc(12*var(--rpx))] sm:px-8 lg:justify-center lg:px-10 lg:pb-8 lg:pt-[calc(5.25rem+env(safe-area-inset-top,_0px))]',
          ].join(' ')}
        >
          <div
            className={[
              'mx-auto grid min-h-0 min-w-0 w-full max-w-[1100px] flex-1 grid-cols-1 gap-[calc(20*var(--rpx))] sm:gap-8',
              'lg:grid-cols-2 lg:grid-rows-1 lg:max-h-[min(88dvh,780px)] lg:items-center lg:gap-x-16 lg:gap-y-0 xl:max-w-[1260px] xl:gap-x-24',
              '2xl:max-w-[1320px] 2xl:gap-x-28',
            ].join(' ')}
          >
            {/* 左栏：背景 + 大标题（保持原样） */}
            <div className="relative flex min-h-0 min-w-0 flex-col overflow-hidden lg:py-1">
              <div
                className="pointer-events-none absolute hidden lg:block"
                aria-hidden
              >
                <div
                  className={[
                    'absolute -left-16 top-8 h-72 w-72 rounded-full opacity-40 blur-3xl',
                    angular ? 'rounded-none' : '',
                  ].join(' ')}
                  style={{
                    background:
                      'radial-gradient(circle, color-mix(in srgb, var(--accent) 55%, transparent) 0%, transparent 70%)',
                  }}
                />
                <div
                  className={[
                    'absolute left-32 top-48 h-44 w-44 bg-[var(--surface-muted)]/35 opacity-50 blur-2xl',
                    angular ? 'rounded-none' : 'rounded-full',
                  ].join(' ')}
                />
              </div>

              <LoginHeroPanel className="min-h-0">
                <div className="mb-0 hidden items-start gap-5 border-b border-line/30 pb-8 lg:flex">
                  <span
                    className={[
                      'mt-1 h-14 w-1.5 shrink-0 bg-gradient-to-b from-accent via-accent/70 to-accent/20',
                      angular ? 'rounded-none' : 'rounded-full',
                    ].join(' ')}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink-soft">
                      当前界面主题
                    </p>
                    <p className="mt-1 font-display text-xl font-semibold tracking-tight text-ink">
                      {themeMeta?.label ?? '—'}
                    </p>
                  </div>
                </div>

                <div className="max-lg:pt-0 lg:pt-7">
                  <h1
                    className={[
                      'flex flex-wrap items-baseline gap-x-2 gap-y-1 text-left text-[clamp(2.125rem,7vw,3rem)] font-bold leading-[1.08] tracking-tight',
                      'drop-shadow-[0_2px_24px_color-mix(in_srgb,var(--surface)_40%,transparent)]',
                      'sm:text-[clamp(2.5rem,6.2vw,3.5rem)]',
                      'lg:text-[clamp(2.25rem,3.8vw,3.1rem)]',
                      'xl:text-[clamp(2.4rem,3.9vw,3.4rem)]',
                      theme === 'pixel' || theme === 'geek' ? 'font-display' : '',
                    ].join(' ')}
                  >
                    <span className="whitespace-nowrap text-ink">进入你的</span>
                    <span className="whitespace-nowrap text-accent">创作舱</span>
                  </h1>
                  {LOGIN_HERO_LINES[theme] ? (
                    <p className="mt-4 max-w-[28rem] text-left text-base leading-relaxed text-ink-soft sm:mt-5 sm:text-lg lg:mt-5 lg:text-[clamp(1rem,1.1vw,1.2rem)] lg:leading-snug xl:text-[clamp(1.05rem,1.15vw,1.3rem)]">
                      {LOGIN_HERO_LINES[theme]}
                    </p>
                  ) : null}
                </div>

                <ul className="mt-0 hidden gap-5 border-t border-line/35 pt-8 lg:mt-8 lg:grid lg:pt-8">
                  {(
                    [
                      ['对话与时间线', '自然语言生图，每轮结果可回看'],
                      ['图库与分类', '作品入库、文件夹与预览管理'],
                      ['主题随行', '顶栏一键切换八种界面气质'],
                    ] as const
                  ).map(([title, desc]) => (
                    <li key={title} className="flex gap-3.5">
                      <span
                        className={[
                          'mt-1.5 h-2 w-2 shrink-0 bg-accent shadow-sm',
                          angular ? 'rounded-none ring-2 ring-line' : 'rounded-full ring-2 ring-accent/25',
                        ].join(' ')}
                        aria-hidden
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-ink">{title}</p>
                        <p className="mt-0.5 text-sm leading-snug text-ink-soft">{desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </LoginHeroPanel>
            </div>

            {/* 右栏：仅登录卡片为新样式 */}
            <div className="relative flex min-h-0 min-w-0 flex-col items-stretch pt-[calc(8*var(--rpx))] max-lg:self-start lg:justify-center lg:overflow-hidden lg:pt-0">
              <LoginAuthCard style={{ animationDelay: '80ms' }}>
                <LoginAuthCardBody>
                  <h2 className="text-[0.9375rem] font-semibold tracking-tight text-ink lg:text-[1.125rem]">
                    账号登录
                  </h2>
                  <p className={['mt-1.5 text-[0.875rem] leading-relaxed', cardMutedMid].join(' ')}>
                    你好，请填写信息完成登录
                  </p>

                  <form className="mt-6 space-y-4" onSubmit={onSubmit}>
                    <div>
                      <label htmlFor="login-user" className="sr-only">
                        用户名或手机号
                      </label>
                      <input
                        id="login-user"
                        autoComplete="username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="请输入用户名或手机号"
                        className={[inputShell, 'lg:text-[0.9375rem]'].join(' ')}
                        disabled={busy}
                      />
                    </div>
                    <div>
                      <label htmlFor="login-pass" className="sr-only">
                        密码
                      </label>
                      <div className="relative">
                        <input
                          ref={passwordInputRef}
                          id="login-pass"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="请输入密码"
                          className={[inputShell, 'pr-[4.25rem] lg:text-[0.9375rem]'].join(' ')}
                          disabled={busy}
                        />
                        <button
                          type="button"
                          className={[
                            'absolute inset-y-0 right-0 flex items-center px-3 transition hover:text-ink',
                            cardMuted,
                          ].join(' ')}
                          onClick={() => setShowPassword((v) => !v)}
                          tabIndex={-1}
                          aria-label={showPassword ? '隐藏密码' : '显示密码'}
                        >
                          {showPassword ? (
                            <svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                              <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                              <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                              <line x1="2" x2="22" y1="2" y2="22" />
                            </svg>
                          ) : (
                            <svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="pt-0.5 text-left">
                      <button
                        type="button"
                        className={[
                          'text-[0.75rem] underline-offset-2 transition hover:text-accent hover:underline',
                          cardMuted,
                        ].join(' ')}
                        onClick={() =>
                          showToast({ message: '如遇登录问题，请联系管理员或稍后再试', variant: 'info' })
                        }
                      >
                        登录遇到问题？
                      </button>
                    </div>

                    <button
                      type="submit"
                      disabled={busy || !username.trim() || !password.trim()}
                      className={[
                        'w-full py-3.5 text-[0.875rem] font-semibold text-white shadow-sm transition lg:py-3 lg:text-[0.9375rem]',
                        'bg-accent hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50',
                        btnRound,
                      ].join(' ')}
                    >
                      {busy ? '登录中…' : '登 录'}
                    </button>
                  </form>

                  <div className="mt-6">
                    <div className="flex items-center gap-3 py-3">
                      <span className={['h-px min-w-0 flex-1', cardDivider].join(' ')} aria-hidden />
                      <span className={['shrink-0 text-center text-[0.75rem]', cardMuted].join(' ')}>
                        或使用微信登录
                      </span>
                      <span className={['h-px min-w-0 flex-1', cardDivider].join(' ')} aria-hidden />
                    </div>

                    <button
                      type="button"
                      className={[
                        'flex min-h-[2.75rem] w-full items-center justify-center gap-2 border py-2.5 text-[0.8125rem] font-semibold transition sm:text-[0.875rem]',
                        fieldRound,
                        'border-[#07C160] bg-[#07C160] text-white shadow-sm hover:bg-[#06ad56]',
                      ].join(' ')}
                      onClick={() => oauthSoon('微信')}
                    >
                      <IconWeChat className="size-5 shrink-0 text-white" />
                      微信登录
                    </button>
                  </div>
                </LoginAuthCardBody>
              </LoginAuthCard>
            </div>
          </div>
        </main>
      </section>
    </div>
  )
}
